import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ValueChangeProperties } from '@rivethealth/data/analytics/event';
import { clean } from './number';
import { DeprecatedTextFieldComponent } from './text-field.component';

/**
 * @deprecated Use riv-number-field instead.
 * @see https://rivethealth.github.io/design-system/?path=/docs/lib-input-number-field--docs
 */
@Component({
  selector: 'deprecated-int-field',
  templateUrl: './int-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeprecatedIntFieldComponent {
  @Input() actionText: string;
  @Input() disabled: boolean;
  @Input() fontSize: string;
  @Input() help: string;
  @Input() label: string;
  @Input() max: number = Infinity;
  @Input() min: number = -Infinity;
  @Input() name: string;
  @Input() readonly: boolean;
  @Input() required: boolean;
  @Input() rightAlignedPrefix: boolean = false;
  @Input() size: string;
  @Input() textAlign: 'left' | 'right' = 'left';
  @Input() warning: boolean = false;

  @Input()
  analyticsEvents: {
    change?: (properties: ValueChangeProperties) => void;
  } = {};

  formattedPlaceholder: string = '';

  @Input()
  set placeholder(v: number) {
    this.formattedPlaceholder = format(v.toString(), this.min, this.max);
  }

  @Input()
  set value(v: number | null) {
    if (clean(this._text, this.min, this.max) === v) return; // Only interrupt the user if the value we are getting is different than what we already have.
    if (v === null) {
      this._text = '';
      return;
    }
    this._text = format(v.toString(), this.min, this.max);
  }

  @Output()
  readonly valueChange = new EventEmitter<number | null>();

  @Output()
  readonly action = new EventEmitter();

  @Output()
  readonly focusOut = new EventEmitter<number | null>();

  @HostListener('focusout', ['$event.target.value'])
  emitCodeChange(currencyValue: string) {
    this.focusOut.emit(+format(currencyValue || '0', this.min, this.max));
  }

  @ViewChild('text-field', { static: false })
  textField: DeprecatedTextFieldComponent;

  private _text: string = '';

  get text(): string {
    return this._text;
  }

  set text(t: string) {
    this._text = t;
    this.valueChange.emit(clean(t, this.min, this.max));
  }

  blur() {
    this._text = format(this._text, this.min, this.max);
  }

  focus() {
    this.textField.focus();
  }

  keydown(evt: KeyboardEvent) {
    if (evt.key === 'Enter') {
      this._text = format(this._text, this.min, this.max);
    }
  }
}

function format(str: string | null, min: number, max: number): string {
  const cleaned = clean(str, min, max);
  if (cleaned === null) return '';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    cleaned,
  );
}
